import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useConfig } from "../hooks/useConfig";
import API, { formatError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { LogOut, Plus, Users, Building2, Briefcase, Shield, Trash2, FileText, Moon, Sun } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

// STATUS_OPTIONS moved to useConfig() inside the component

function getStatusStyle(status, isDark) {
  if (isDark) {
    const map = {
      Pending: { background: 'rgba(234,179,8,0.12)', color: '#fbbf24', border: '1px solid rgba(234,179,8,0.25)' },
      Interested: { background: 'rgba(5,150,105,0.12)', color: '#34d399', border: '1px solid rgba(5,150,105,0.25)' },
      Shortlisted: { background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' },
      "Interview Scheduled": { background: 'rgba(168,85,247,0.12)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.25)' },
      "Offer Received": { background: 'rgba(5,150,105,0.12)', color: '#34d399', border: '1px solid rgba(5,150,105,0.25)' },
      Rejected: { background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' },
    };
    return map[status] || { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' };
  }
  const map = {
    Pending: { background: 'rgba(234,179,8,0.1)', color: '#d97706', border: '1px solid rgba(234,179,8,0.25)' },
    Interested: { background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' },
    Shortlisted: { background: 'rgba(59,130,246,0.1)', color: '#2563eb', border: '1px solid rgba(59,130,246,0.25)' },
    "Interview Scheduled": { background: 'rgba(59,130,246,0.1)', color: '#2563eb', border: '1px solid rgba(59,130,246,0.25)' },
    "Offer Received": { background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' },
    Rejected: { background: 'rgba(239,68,68,0.1)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.25)' },
  };
  return map[status] || { background: 'rgba(0,0,0,0.04)', color: '#64748b', border: '1px solid rgba(0,0,0,0.1)' };
}

export default function AdminPanel() {
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { config } = useConfig();
  const navigate = useNavigate();

  const STATUS_OPTIONS = config.application_statuses;

  const [students, setStudents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealedEmails, setRevealedEmails] = useState({});

  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [resumeStudentName, setResumeStudentName] = useState("");
  const [resumeLoading, setResumeLoading] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [rolesInput, setRolesInput] = useState("");
  const [skillsInput, setSkillsInput] = useState("");
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyMsg, setCompanyMsg] = useState("");

  const [assignStudent, setAssignStudent] = useState("");
  const [assignCompany, setAssignCompany] = useState("");
  const [assignRole, setAssignRole] = useState("");
  const [assignMsg, setAssignMsg] = useState("");

  // Theme-aware styles
  const pageBg = isDark ? '#0a0a0f' : '#f8f9fa';
  const headerBg = isDark ? 'rgba(10,10,15,0.9)' : 'rgba(255,255,255,0.95)';
  const headerBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textPrimary = isDark ? '#ffffff' : '#0a0a0f';
  const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const textMuted = isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';
  const cardShadow = isDark ? 'none' : '0 1px 8px rgba(0,0,0,0.05)';
  const glassCard = { background: cardBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: cardBorder, boxShadow: cardShadow };
  const inputStyle = isDark
    ? { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }
    : { background: 'rgba(0,0,0,0.04)', borderColor: 'rgba(0,0,0,0.1)', color: '#0a0a0f' };
  const selectStyle = isDark
    ? { background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.12)' }
    : { background: 'rgba(0,0,0,0.04)', borderColor: 'rgba(0,0,0,0.1)', color: '#0a0a0f', border: '1px solid rgba(0,0,0,0.1)' };
  const tableRowBorder = isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.04)';
  const tableHeadBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';
  const accent = '#059669';
  const accentDark = '#047857';
  const accentLight = isDark ? 'rgba(5,150,105,0.12)' : 'rgba(5,150,105,0.08)';
  const accentBorder = 'rgba(5,150,105,0.25)';
  const optionBg = isDark ? '#0d0d14' : '#ffffff';

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c, a, an] = await Promise.all([
        API.get("/api/admin/students"),
        API.get("/api/admin/companies"),
        API.get("/api/admin/applications"),
        API.get("/api/admin/analytics"),
      ]);
      setStudents(s.data);
      setCompanies(c.data);
      setApplications(a.data);
      setAnalytics(an.data);
    } catch (e) {
      console.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.history.replaceState(null, "", "/");
    window.history.pushState(null, "", "/admin");
  }, []);

  useEffect(() => { loadAll(); }, []);

  const handleLogout = () => { logout(); window.location.replace("/"); };

  const handleViewResume = async (student) => {
    setResumeStudentName(student.name);
    setResumeLoading(true);
    setResumeModalOpen(true);
    try {
      const res = await API.get(`/api/admin/students/${student.user_id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setResumeUrl(url);
    } catch (e) {
      setResumeUrl(null);
    } finally {
      setResumeLoading(false);
    }
  };

  const handleCloseResume = useCallback(() => {
    setResumeModalOpen(false);
    if (resumeUrl) {
      URL.revokeObjectURL(resumeUrl);
      setResumeUrl(null);
    }
  }, [resumeUrl]);

  const handleAddCompany = async (e) => {
    e.preventDefault();
    setCompanyLoading(true);
    setCompanyMsg("");
    try {
      await API.post("/api/admin/companies", {
        name: companyName,
        roles_offered: rolesInput.split(",").map(r => r.trim()).filter(Boolean),
        required_skills: skillsInput.split(",").map(s => s.trim()).filter(Boolean),
      });
      setCompanyMsg("Company added successfully!");
      setCompanyName(""); setRolesInput(""); setSkillsInput("");
      const res = await API.get("/api/admin/companies");
      setCompanies(res.data);
    } catch (e) {
      setCompanyMsg(formatError(e));
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleAssignStudent = async (e) => {
    e.preventDefault();
    setAssignMsg("");
    try {
      await API.post("/api/admin/assign-student", {
        student_id: assignStudent,
        company_id: assignCompany,
        role: assignRole,
      });
      setAssignMsg("Student assigned successfully!");
      setAssignStudent(""); setAssignCompany(""); setAssignRole("");
      const res = await API.get("/api/admin/applications");
      setApplications(res.data);
    } catch (e) {
      setAssignMsg(formatError(e));
    }
  };

  const handleStatusChange = async (appId, status) => {
    try {
      await API.put(`/api/recruiter/applications/${appId}/status`, { status });
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status } : a));
    } catch (e) {
      console.error(formatError(e));
    }
  };

  const handleDeleteStudent = async (userId) => {
    try {
      await API.delete(`/api/admin/students/${userId}`);
      setStudents(prev => prev.filter(s => s.user_id !== userId));
    } catch (e) {
      console.error(formatError(e));
    }
  };

  return (
    <div className="min-h-screen" style={{ background: pageBg }}>
      {/* Header */}
      <header className="sticky top-0 z-10" style={{ background: headerBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: `1px solid ${headerBorder}` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})` }}>
              <span className="text-white font-bold text-xs">W</span>
            </div>
            <span className="font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>WonStepCareer Admin</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium border hidden sm:flex items-center gap-1" style={{ background: isDark ? 'rgba(5,150,105,0.15)' : 'rgba(5,150,105,0.1)', borderColor: 'rgba(5,150,105,0.25)', color: accent }}>
              <Shield size={10} /> Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={e => toggleTheme(e)}
              aria-label="Toggle theme"
              style={{ width: '30px', height: '30px', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSecondary }}
            >
              {isDark ? <Sun size={13} /> : <Moon size={13} />}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: textMuted, background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Admin Dashboard</h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>Manage students, companies, and placements</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {[
            { icon: <Users size={16} />, label: "Students", value: students.length },
            { icon: <Building2 size={16} />, label: "Companies", value: companies.length },
            { icon: <Briefcase size={16} />, label: "Applications", value: applications.length },
            { icon: <Shield size={16} />, label: "Offers Made", value: analytics?.offers_made ?? '—' },
            { icon: <FileText size={16} />, label: "Placement Rate", value: analytics ? `${analytics.placement_rate}%` : '—' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl p-4 text-center glass-card" style={glassCard}>
              <div className="flex justify-center mb-1" style={{ color: accent }}>{s.icon}</div>
              <div className="text-2xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>{s.value}</div>
              <div className="text-xs" style={{ color: textMuted }}>{s.label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <Tabs defaultValue="students">
            <TabsList className="mb-6" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
              <TabsTrigger value="students" data-testid="tab-students">
                Students ({students.length})
              </TabsTrigger>
              <TabsTrigger value="companies" data-testid="tab-companies">
                Companies ({companies.length})
              </TabsTrigger>
              <TabsTrigger value="applications" data-testid="tab-applications">
                Applications ({applications.length})
              </TabsTrigger>
            </TabsList>

            {/* Students Tab */}
            <TabsContent value="students">
              <Card style={glassCard}>
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>All Students</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow style={{ borderBottom: tableHeadBorder }}>
                        <TableHead style={{ color: textSecondary }}>Name</TableHead>
                        <TableHead style={{ color: textSecondary }}>Email</TableHead>
                        <TableHead style={{ color: textSecondary }}>College</TableHead>
                        <TableHead style={{ color: textSecondary }}>Branch</TableHead>
                        <TableHead style={{ color: textSecondary }}>CGPA</TableHead>
                        <TableHead style={{ color: textSecondary }}>Role</TableHead>
                        <TableHead style={{ color: textSecondary }}>Skills</TableHead>
                        <TableHead style={{ color: textSecondary }}>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {students.length === 0 ? (
                        <TableRow><TableCell colSpan={8} className="text-center py-8" style={{ color: textMuted }}>No students registered yet</TableCell></TableRow>
                      ) : students.map(s => (
                        <TableRow key={s.id} style={{ borderBottom: tableRowBorder }} data-testid={`admin-student-row-${s.id}`}
                          onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <TableCell className="font-medium" style={{ color: textPrimary }}>{s.name}</TableCell>
                          <TableCell className="text-sm">
                            {revealedEmails[s.user_id] ? (
                              <span className="flex items-center gap-1.5">
                                <span className="font-mono text-xs" style={{ color: textSecondary }}>{s.email || "—"}</span>
                                <button
                                  onClick={() => setRevealedEmails(prev => ({ ...prev, [s.user_id]: false }))}
                                  className="text-xs underline"
                                  style={{ color: textMuted }}
                                >Hide</button>
                              </span>
                            ) : (
                              <button
                                onClick={() => setRevealedEmails(prev => ({ ...prev, [s.user_id]: true }))}
                                className="text-xs font-medium hover:underline"
                                style={{ color: accent }}
                                data-testid={`view-email-btn-${s.user_id}`}
                              >View</button>
                            )}
                          </TableCell>
                          <TableCell className="text-sm" style={{ color: textSecondary }}>{s.college}</TableCell>
                          <TableCell className="text-sm" style={{ color: textSecondary }}>{s.branch}</TableCell>
                          <TableCell style={{ color: textSecondary }}>{s.cgpa}</TableCell>
                          <TableCell>
                            {s.classified_role && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', color: textSecondary, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}` }}>{s.classified_role}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(s.extracted_skills || []).slice(0, 3).map(sk => (
                                <span key={sk} className="px-1.5 py-0.5 text-xs rounded" style={{ background: isDark ? 'rgba(5,150,105,0.12)' : accent, color: isDark ? '#34d399' : '#ffffff' }}>{sk}</span>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {s.has_resume && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 gap-1 text-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
                                  onClick={() => handleViewResume(s)}
                                  data-testid={`view-resume-btn-${s.user_id}`}
                                >
                                  <FileText size={13} />
                                  <span className="text-xs">Resume</span>
                                </Button>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 gap-1 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                    data-testid={`delete-student-btn-${s.user_id}`}
                                  >
                                    <Trash2 size={13} />
                                    <span className="text-xs">Remove</span>
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent style={{ background: isDark ? 'rgba(15,15,22,0.97)' : '#ffffff', backdropFilter: 'blur(30px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle style={{ color: textPrimary }}>Remove Student</AlertDialogTitle>
                                    <AlertDialogDescription style={{ color: textSecondary }}>
                                      Are you sure you want to remove <strong style={{ color: textPrimary }}>{s.name}</strong>? This permanently deletes their account, profile, resume, and all applications.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel style={{ background: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', color: textSecondary }}>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteStudent(s.user_id)}
                                      className="font-semibold"
                                      style={{ background: 'linear-gradient(135deg, #ef4444, #b91c1c)', color: 'white' }}
                                    >
                                      Remove Student
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Assign Student to Company */}
              <Card className="mt-6" style={glassCard}>
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Assign Student to Company Pipeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAssignStudent} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: textSecondary }}>Student</Label>
                      <select
                        value={assignStudent}
                        onChange={e => setAssignStudent(e.target.value)}
                        required
                        className="w-full px-3 py-2 rounded-md text-sm focus:outline-none"
                        style={selectStyle}
                        data-testid="assign-student-select"
                      >
                        <option value="" style={{ background: optionBg }}>Select student</option>
                        {students.map(s => (
                          <option key={s.user_id} value={s.user_id} style={{ background: optionBg }}>{s.name} — {s.college}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: textSecondary }}>Company</Label>
                      <select
                        value={assignCompany}
                        onChange={e => setAssignCompany(e.target.value)}
                        required
                        className="w-full px-3 py-2 rounded-md text-sm focus:outline-none"
                        style={selectStyle}
                        data-testid="assign-company-select"
                      >
                        <option value="" style={{ background: optionBg }}>Select company</option>
                        {companies.map(c => (
                          <option key={c.id} value={c.id} style={{ background: optionBg }}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: textSecondary }}>Role (optional)</Label>
                      <Input value={assignRole} onChange={e => setAssignRole(e.target.value)} placeholder="e.g. SDE-1" className="text-sm placeholder:text-gray-400" style={inputStyle} data-testid="assign-role-input" />
                    </div>
                    <div className="flex items-end">
                      <button type="submit" className="w-full py-2 px-4 rounded-md text-sm font-semibold flex items-center justify-center gap-2" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white', border: 'none', cursor: 'pointer' }} data-testid="assign-submit-btn">
                        <Plus size={14} /> Assign
                      </button>
                    </div>
                  </form>
                  {assignMsg && (
                    <p className="mt-3 text-sm p-3 rounded-lg" style={assignMsg.includes("success") ? { background: 'rgba(5,150,105,0.1)', color: isDark ? '#34d399' : '#065f46', border: '1px solid rgba(5,150,105,0.2)' } : { background: 'rgba(239,68,68,0.1)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="assign-msg">
                      {assignMsg}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Companies Tab */}
            <TabsContent value="companies">
              <Card className="mb-6" style={glassCard}>
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Add Company Listing</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddCompany} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: textSecondary }}>Company Name</Label>
                        <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="e.g. Google India" required className="text-sm placeholder:text-gray-400" style={inputStyle} data-testid="company-name-input" />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: textSecondary }}>Roles Offered (comma-separated)</Label>
                        <Input value={rolesInput} onChange={e => setRolesInput(e.target.value)} placeholder="SDE-1, Data Analyst" required className="text-sm placeholder:text-gray-400" style={inputStyle} data-testid="company-roles-input" />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: textSecondary }}>Required Skills (comma-separated)</Label>
                        <Input value={skillsInput} onChange={e => setSkillsInput(e.target.value)} placeholder="Python, SQL, React" required className="text-sm placeholder:text-gray-400" style={inputStyle} data-testid="company-skills-input" />
                      </div>
                    </div>
                    <button type="submit" disabled={companyLoading} className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white', border: 'none', cursor: companyLoading ? 'not-allowed' : 'pointer', opacity: companyLoading ? 0.7 : 1, boxShadow: `0 2px 12px rgba(5,150,105,0.3)` }} data-testid="add-company-btn">
                      <Plus size={14} /> {companyLoading ? "Adding..." : "Add Company"}
                    </button>
                    {companyMsg && (
                      <p className="text-sm p-3 rounded-lg" style={companyMsg.includes("success") ? { background: 'rgba(5,150,105,0.1)', color: isDark ? '#34d399' : '#065f46', border: '1px solid rgba(5,150,105,0.2)' } : { background: 'rgba(239,68,68,0.1)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="company-msg">
                        {companyMsg}
                      </p>
                    )}
                  </form>
                </CardContent>
              </Card>

              <Card style={glassCard}>
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Companies ({companies.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {companies.length === 0 ? (
                    <div className="text-center py-8">
                      <Building2 size={32} className="mx-auto mb-3" style={{ color: textMuted }} />
                      <p className="text-sm" style={{ color: textMuted }}>No companies added yet.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {companies.map(c => (
                        <div key={c.id} className="p-4 rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)' }} data-testid={`company-card-${c.id}`}>
                          <h3 className="font-semibold mb-2" style={{ color: textPrimary }}>{c.name}</h3>
                          <div className="mb-2">
                            <p className="text-xs mb-1" style={{ color: textMuted }}>Roles</p>
                            <div className="flex flex-wrap gap-1">
                              {(c.roles_offered || []).map(r => (
                                <span key={r} className="px-2 py-0.5 text-xs rounded-full" style={{ background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', color: textSecondary, border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>{r}</span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs mb-1" style={{ color: textMuted }}>Required Skills</p>
                            <div className="flex flex-wrap gap-1">
                              {(c.required_skills || []).map(s => (
                                <span key={s} className="px-2 py-0.5 text-xs rounded-full" style={{ background: isDark ? 'rgba(5,150,105,0.12)' : accent, color: isDark ? '#34d399' : '#ffffff' }}>{s}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications">
              <Card style={glassCard}>
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>All Applications</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow style={{ borderBottom: tableHeadBorder }}>
                        <TableHead style={{ color: textSecondary }}>Student</TableHead>
                        <TableHead style={{ color: textSecondary }}>Company</TableHead>
                        <TableHead style={{ color: textSecondary }}>Role</TableHead>
                        <TableHead style={{ color: textSecondary }}>Applied On</TableHead>
                        <TableHead style={{ color: textSecondary }}>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applications.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8" style={{ color: textMuted }}>No applications yet.</TableCell>
                        </TableRow>
                      ) : applications.map(app => (
                        <TableRow key={app.id} style={{ borderBottom: tableRowBorder }} data-testid={`admin-app-row-${app.id}`}
                          onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <TableCell className="font-medium text-sm" style={{ color: textPrimary }}>{app.student_name || app.student_id}</TableCell>
                          <TableCell className="text-sm" style={{ color: textSecondary }}>{app.company_name || app.company_id}</TableCell>
                          <TableCell className="text-sm" style={{ color: textSecondary }}>{app.role || "—"}</TableCell>
                          <TableCell className="text-sm" style={{ color: textMuted }}>
                            {app.created_at ? new Date(app.created_at).toLocaleDateString("en-IN") : "—"}
                          </TableCell>
                          <TableCell>
                            <span className="inline-block text-xs px-2.5 py-1 rounded-full font-medium" style={getStatusStyle(app.status, isDark)} data-testid={`admin-status-badge-${app.id}`}>
                              {app.status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Resume Preview Modal */}
      <Dialog open={resumeModalOpen} onOpenChange={handleCloseResume}>
        <DialogContent className="max-w-4xl w-full h-[85vh] flex flex-col p-0 gap-0" style={{ background: isDark ? 'rgba(15,15,22,0.97)' : '#ffffff', backdropFilter: 'blur(30px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
          <DialogHeader className="px-6 py-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}` }}>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>
              <FileText size={18} style={{ color: accent }} />
              {resumeStudentName}'s Resume
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 p-4 overflow-hidden">
            {resumeLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
              </div>
            ) : resumeUrl ? (
              <iframe
                src={resumeUrl}
                className="w-full h-full rounded-lg"
                style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}` }}
                title="Resume Preview"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-3" style={{ color: textMuted }}>
                <FileText size={48} style={{ opacity: 0.2 }} />
                <p className="text-sm">No resume found for this student.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
